/**
 * Centro de notificaciones — campana en el header.
 *
 * Cambios v2 (auditoría):
 *   1. **Filtro estricto por usuario**: cada alerta declara `paraRoles[]`
 *      y/o `paraEmails[]`. Solo se muestran las que aplican al usuario
 *      logueado. ADMIN_PMIS las ve todas (modo soporte).
 *   2. **ID estable por alerta**: `<TIPO>:<CODIGO>` (ej. `VENCIDA:PO-001`)
 *      para poder marcar como leídas / borradas sin perder identidad
 *      entre re-cómputos.
 *   3. **Acciones por alerta**: ✓ marcar leída, ✕ borrar. Persistido en
 *      localStorage por usuario (ver `notificacionesEstado.ts`).
 *   4. **Bulk**: marcar todas como leídas / restaurar borradas.
 *
 * Las alertas se computan en vivo desde el estado del sistema (POs/PRs/
 * SOs/RDs). Si la alerta deja de aplicar (ej. la PO ya no está vencida
 * porque se entregó), desaparece — el sistema es self-healing.
 */

import { useMemo, useState } from "react";
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  Button,
  Text,
} from "@fluentui/react-components";
import {
  AlertRegular,
  CheckmarkRegular,
  DismissRegular,
  EyeRegular,
} from "@fluentui/react-icons";
import { usePos } from "@features/po/poQueries";
import { usePrs } from "@features/pr/prQueries";
import { useSos } from "@features/so/soQueries";
import { useRds } from "@features/rd/rdQueries";
import { useRegistrosRecursos } from "@features/recursos/recursosQueries";
import { otsDelContratista } from "@features/contratos/scopeContratos";
import { useUsuariosActivos } from "@features/autoridades/autoridadesQueries";
import { useContratos } from "@features/contratos/contratosQueries";
import { interventoresDeLaSo, aprobadoresVoBoDisciplina } from "@core/auth/scope";
import { detectarSosVencidas, etiquetaConceptoInt } from "@features/so/soRules";
import { useAuth } from "@core/auth/useAuth";
import { useUsuarioContexto } from "@core/auth/useUsuarioContexto";
import { puedeVerAlerta, alertasAjusteRr, type Alerta } from "./campanaRules";
import {
  diasRealesEntrega,
  estadoEntregaPo,
} from "@features/po/poRules";
import {
  slaLimitePr,
  diasGestionPr,
  estadoSlaPr,
} from "@features/pr/prRules";
import { fmtDateShort, fmtDateTimeShort } from "@/lib/formatters";
import {
  borrarAlerta,
  marcarLeida,
  marcarTodasLeidas,
  restaurarBorradas,
  useNotificacionesEstado,
} from "./notificacionesEstado";

export function NotificacionesMenu() {
  const { user } = useAuth();
  const userCtx = useUsuarioContexto();
  const tenantId = user?.tenantId ?? "";
  const { data: pos } = usePos(tenantId);
  const { data: prs } = usePrs(tenantId);
  const { data: sos } = useSos(tenantId);
  const { data: rds } = useRds(tenantId);
  const { data: usuarios } = useUsuariosActivos(tenantId);
  const { data: registrosRr } = useRegistrosRecursos(tenantId);
  const { data: contratos } = useContratos(tenantId);
  const estadoNotif = useNotificacionesEstado(user?.email ?? "");
  const leidas = useMemo(() => new Set(estadoNotif.leidas), [estadoNotif.leidas]);
  const borradas = useMemo(() => new Set(estadoNotif.borradas), [estadoNotif.borradas]);
  const [verBorradas, setVerBorradas] = useState(false);

  const items = useMemo<Alerta[]>(() => {
    const result: Alerta[] = [];
    const hoy = new Date();
    const fechaHora = fmtDateTimeShort(hoy);

    // ── PO vencidas + por vencer ────────────────────────────────────
    for (const p of pos ?? []) {
      const e = estadoEntregaPo(p);
      if (e === "vencida") {
        const dias = diasRealesEntrega(p);
        result.push({
          id: `VENCIDA:${p.codigo}`,
          tipo: "VENCIDA",
          label: `${p.codigo} — VENCIDA`,
          detalle: `${p.proveedorNombre}${dias !== null ? ` · ${dias}d desde emisión` : ""}`,
          color: "#C0392B",
          fechaHora,
          generadoPor: p.createdBy,
          dirigidoA: "Abastecimiento",
          paraRoles: ["ABAS", "JEFE", "GTE"],
        });
        continue;
      }
      if ((p.estado === "EMITIDA" || p.estado === "EN_ESPERA_ENTREGA") && p.fechaEntregaCalc) {
        const f = new Date(p.fechaEntregaCalc + "T00:00:00").getTime();
        const diff = Math.round((f - hoy.getTime()) / (1000 * 60 * 60 * 24));
        if (diff >= 0 && diff <= 7) {
          result.push({
            id: `POR_VENCER:${p.codigo}`,
            tipo: "POR_VENCER",
            label: `${p.codigo} — Entrega en ${diff}d`,
            detalle: `${p.proveedorNombre} · vence ${fmtDateShort(p.fechaEntregaCalc)}`,
            color: "#E08600",
            fechaHora,
            generadoPor: p.createdBy,
            dirigidoA: "Abastecimiento",
            paraRoles: ["ABAS"],
          });
        }
      }
    }

    // ── PRs en alerta SLA o pendientes de aprobación ───────────────
    for (const pr of prs ?? []) {
      if (pr.estado === "CREADA_NO_APROBADA") {
        result.push({
          id: `PR_PENDIENTE:${pr.codigo}`,
          tipo: "PR_PENDIENTE",
          label: `${pr.codigo} — Pendiente aprobación`,
          detalle: `${pr.concepto.slice(0, 60)}${pr.concepto.length > 60 ? "…" : ""}`,
          color: "#7B1FA2",
          fechaHora,
          generadoPor: pr.solicitadoPor,
          dirigidoA: "Gerencia / JEFE (aprobación)",
          paraRoles: ["JEFE", "GTE"],
          paraEmails: [pr.solicitadoPor],
        });
        continue;
      }
      if (pr.estado === "ADJ_TOTAL" || pr.estado === "CANCELADA") continue;
      const sla = slaLimitePr(pr.tipoSuministro, pr.leadTimeClasif);
      const gestion = diasGestionPr(pr.fechaAprobacionGerencia, pr.fechaEmisionPo);
      if (!gestion) continue;
      const est = estadoSlaPr(gestion.diasTranscurridos, sla.dias);
      if (est === "excedido" || est === "alerta") {
        result.push({
          id: `PR_ALERTA:${pr.codigo}`,
          tipo: "PR_ALERTA",
          label: `${pr.codigo} — SLA ${est === "excedido" ? "excedido" : "en alerta"}`,
          detalle: `${gestion.diasTranscurridos}d transcurridos vs ${sla.dias}d SLA (${sla.descripcion})`,
          color: est === "excedido" ? "#C0392B" : "#E08600",
          fechaHora,
          generadoPor: pr.solicitadoPor,
          dirigidoA: pr.comprador ?? "Abastecimiento",
          paraRoles: ["ABAS", "JEFE"],
          paraEmails: [pr.comprador, pr.solicitadoPor].filter((s): s is string => !!s),
        });
      }
    }

    // ── SOs vencidas (plazo cumplido sin cierre) ───────────────────
    for (const s of detectarSosVencidas(sos ?? [])) {
      result.push({
        id: `SO_VENCIDA:${s.codigo}`,
        tipo: "SO_VENCIDA",
        label: `${s.codigo} — SO vencida`,
        detalle: `${s.nombre} · ${s.contratistaNombre ?? s.contratistaId} · venció ${fmtDateShort(s.fechaFin)}`,
        color: "#C0392B",
        fechaHora,
        generadoPor: s.createdBy,
        dirigidoA: s.createdBy,
        paraRoles: ["JEFE", "GTE"],
        paraEmails: [s.createdBy],
      });
    }

    // ── SOs por cotizar / por aprobar / aprobadas ──────────────────
    for (const s of sos ?? []) {
      // Retenida por visto bueno de disciplina: no se le anuncia al contratista,
      // pero SÍ hay que avisarle a quien puede liberarla. Sin este aviso la OT
      // quedaba sin cola y podía permanecer invisible indefinidamente.
      if (s.estado === "EN_COTIZACION" && s.pendienteVoBoDisciplina) {
        const cm = (contratos ?? []).find((c) => c.id === s.contratoMarcoId);
        result.push({
          id: `SO_PENDIENTE_VOBO:${s.codigo}`,
          tipo: "SO_PENDIENTE_VOBO",
          label: `${s.codigo} — Pendiente de su visto bueno`,
          detalle: `Creada por otra disciplina sobre un contrato de ${cm?.disciplina ?? "su disciplina"}. El contratista no la recibirá hasta que usted la autorice.`,
          color: "#B45309",
          fechaHora,
          generadoPor: s.createdBy,
          dirigidoA: cm?.disciplina ?? "disciplina del contrato",
          paraEmails: aprobadoresVoBoDisciplina(cm, usuarios ?? []),
          // Red de seguridad: si la cadena no resuelve a nadie —contrato sin
          // disciplina, o creado por "system" en los datos antiguos— el aviso
          // igual llega a la gerencia, que siempre puede liberar. Sin esto la OT
          // volvería a quedarse sin cola, que es justo lo que se está corrigiendo.
          ...(aprobadoresVoBoDisciplina(cm, usuarios ?? []).length === 0 && {
            paraRoles: ["JEFE", "GTE"] as const,
          }),
        });
        continue;
      }
      // Una OT liberada sí se le anuncia al contratista para que cotice.
      if (s.estado === "EN_COTIZACION" && !s.pendienteVoBoDisciplina) {
        // El contratista de la SO debe cotizar. `paraEmpresa` acota la alerta a
        // su empresa: antes se notificaba solo por rol y el contratista de
        // CUALQUIER empresa la veía.
        result.push({
          id: `SO_PARA_COTIZAR:${s.codigo}`,
          tipo: "SO_PARA_COTIZAR",
          paraEmpresa: s.contratistaNombre ?? "",
          label: `${s.codigo} — Pendiente de cotización`,
          detalle: `${s.nombre.slice(0, 60)}${s.nombre.length > 60 ? "…" : ""} · entrega ${fmtDateShort(s.fechaFin)}`,
          color: "#2A6FB0",
          fechaHora,
          generadoPor: s.createdBy,
          dirigidoA: s.contratistaNombre ?? s.contratistaId,
          paraRoles: ["PLANNER", "ADOR"],
        });
        continue;
      }
      if (s.estado === "EN_REVISION") {
        result.push({
          id: `SO_PENDIENTE_APROB:${s.codigo}`,
          tipo: "SO_PENDIENTE_APROB",
          label: `${s.codigo} — Cotización por aprobar`,
          detalle: `${s.nombre.slice(0, 60)}${s.nombre.length > 60 ? "…" : ""} · ${s.contratistaNombre ?? s.contratistaId} · ${etiquetaConceptoInt(s).texto}`,
          color: "#7B1FA2",
          fechaHora,
          generadoPor: s.contratistaNombre ?? s.contratistaId,
          dirigidoA: s.createdBy,
          paraRoles: ["PL", "PL2", "CFE", "JEFE"],
          // El interventor y su back deben enterarse de que hay una cotización
          // por conceptuar: sin este aviso, la revisión de Interventoría
          // dependía de que entraran a mirar por su cuenta.
          paraEmails: [s.createdBy, ...interventoresDeLaSo(s, usuarios ?? [])],
        });
        continue;
      }
      if (s.estado === "APROBADA") {
        // SO aprobada: avisa al contratista e interventor que pueden
        // iniciar el reporte diario.
        result.push({
          id: `SO_APROBADA:${s.codigo}`,
          tipo: "SO_APROBADA",
          paraEmpresa: s.contratistaNombre ?? "",
          label: `${s.codigo} — SO aprobada`,
          detalle: `${s.nombre.slice(0, 60)}${s.nombre.length > 60 ? "…" : ""} · ya puede registrar el reporte diario`,
          color: "#2A954F",
          fechaHora,
          generadoPor: s.createdBy,
          dirigidoA: `${s.contratistaNombre ?? s.contratistaId}${s.interventorId ? ` / ${s.interventorId}` : ""}`,
          paraRoles: ["PLANNER", "ADOR"],
          paraEmails: [s.interventorId].filter((x): x is string => !!x),
        });
      }
    }

    // ── RDs en flujo de revisión ───────────────────────────────────
    for (const rd of rds ?? []) {
      const so = (sos ?? []).find((s) => s.id === rd.soId);
      const soCod = so?.codigo ?? rd.soId;
      const fechaRd = fmtDateShort(rd.fecha);
      if (rd.estado === "EN_REVISION") {
        // Revisión en PARALELO: el Interventor conceptúa y el PL2 aprueba, sin
        // turnos. Ambas alertas se emiten a la vez mientras el RD siga en
        // revisión; la del PL2 exigía antes un nivel "PL" que ya no se asigna,
        // por lo que nunca aparecía.
        const intYaConceptuo = rd.calificacionInt != null;
        if (!intYaConceptuo && so?.interventorId) {
          result.push({
            id: `RD_POR_REVISAR_INT:${rd.id}`,
            tipo: "RD_POR_REVISAR_INT",
            label: `${soCod} — RD ${fechaRd} por revisar`,
            detalle: "Reporte diario enviado por el contratista — pendiente de tu revisión",
            color: "#2A6FB0",
            fechaHora,
            generadoPor: rd.updatedBy || rd.createdBy,
            dirigidoA: so.interventorId,
            // Titular y back: el back está autorizado a conceptuar el reporte,
            // así que también debe recibir el aviso.
            paraEmails: interventoresDeLaSo(so, usuarios ?? []),
          });
        }
        if (so?.createdBy) {
          result.push({
            id: `RD_POR_APROBAR_PL2:${rd.id}`,
            tipo: "RD_POR_APROBAR_PL2",
            label: `${soCod} — RD ${fechaRd} por aprobar`,
            detalle: intYaConceptuo
              ? "Reporte diario con concepto del Interventor — pendiente de su aprobación e integración"
              : "Reporte diario pendiente de su aprobación — el Interventor aún no deja concepto",
            color: "#7B1FA2",
            fechaHora,
            generadoPor: rd.updatedBy || rd.createdBy,
            dirigidoA: so.createdBy,
            paraEmails: [so.createdBy],
          });
        }
      }
      if (
        rd.estado === "BORRADOR" &&
        (rd.observacionesInt || rd.observacionesPl2)
      ) {
        result.push({
          id: `RD_DEVUELTO:${rd.id}`,
          tipo: "RD_DEVUELTO",
          paraEmpresa: so?.contratistaNombre ?? "",
          label: `${soCod} — RD ${fechaRd} devuelto`,
          detalle: `Reporte diario devuelto para corrección: ${rd.observacionesPl2 || rd.observacionesInt}`,
          color: "#E08600",
          fechaHora,
          generadoPor: rd.updatedBy || rd.createdBy,
          dirigidoA: so?.contratistaNombre ?? rd.createdBy,
          paraRoles: ["PLANNER", "ADOR"],
        });
      }
    }

    // Ciclo de ajuste del Registro de recursos. El interventor que conceptúa no
    // sale de una sola OT —el registro es del contratista y abarca todas sus
    // OT— así que se avisa a los INT de las OT de ese contratista que tienen
    // reporte esa fecha, que son quienes verán cambiar sus números.
    result.push(
      ...alertasAjusteRr(registrosRr ?? [], (contratistaId, fecha) => {
        const mias = new Set(otsDelContratista(contratistaId, sos ?? []).map((s) => s.id));
        const conReporte = new Set(
          (rds ?? []).filter((r) => r.fecha === fecha && mias.has(r.soId)).map((r) => r.soId),
        );
        const correos = new Set<string>();
        for (const so of sos ?? []) {
          if (!conReporte.has(so.id)) continue;
          for (const e of interventoresDeLaSo(so, usuarios ?? [])) correos.add(e);
        }
        return [...correos];
      }),
    );

    return result;
  }, [pos, prs, sos, rds, usuarios, contratos, registrosRr]);

  // ── Filtro final por usuario + estado leído/borrado ────────────────
  const visibles = useMemo(() => {
    return items.filter((a) => {
      if (
        !puedeVerAlerta(
          user
            ? {
                email: user.email,
                role: user.role,
                ...(userCtx?.empresa !== undefined && { empresa: userCtx.empresa }),
              }
            : null,
          a,
        )
      )
        return false;
      if (borradas.has(a.id) && !verBorradas) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se usan user?.email/role (primitivos estables) a propósito; depender de `user` recalcularía en cada render
  }, [items, user?.email, user?.role, borradas, verBorradas]);

  const conteoSinLeer = visibles.filter((a) => !leidas.has(a.id) && !borradas.has(a.id)).length;
  const totalVisible = visibles.length;
  const emailActual = user?.email ?? "";

  const todasLeidas = () => {
    marcarTodasLeidas(
      emailActual,
      visibles.map((a) => a.id),
    );
  };

  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          icon={<AlertRegular />}
          style={{ color: "white", position: "relative" }}
          title={conteoSinLeer > 0 ? `${conteoSinLeer} alertas sin leer` : "Sin alertas pendientes"}
        >
          {conteoSinLeer > 0 && (
            <span
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                background: "#C0392B",
                color: "white",
                borderRadius: 10,
                padding: "0 6px",
                fontSize: 10,
                fontWeight: 700,
                minWidth: 16,
                textAlign: "center",
              }}
            >
              {conteoSinLeer}
            </span>
          )}
        </Button>
      </MenuTrigger>
      <MenuPopover style={{ minWidth: 380, maxWidth: 460, maxHeight: 560, overflow: "auto" }}>
        <div
          style={{
            padding: "0.6rem 0.75rem",
            borderBottom: "1px solid #eee",
            background: "#003A70",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <strong>Notificaciones</strong>
          <span style={{ fontSize: 11, opacity: 0.85 }}>
            {conteoSinLeer} sin leer · {totalVisible} {totalVisible === 1 ? "alerta" : "alertas"}
          </span>
        </div>

        {/* Toolbar */}
        {totalVisible > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "6px 10px",
              background: "#F9FAFB",
              borderBottom: "1px solid #E5E7EB",
              fontSize: 11,
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              appearance="subtle"
              icon={<CheckmarkRegular />}
              onClick={todasLeidas}
              disabled={conteoSinLeer === 0}
            >
              Marcar todas leídas
            </Button>
            <Button
              size="small"
              appearance="subtle"
              icon={<EyeRegular />}
              onClick={() => setVerBorradas((v) => !v)}
            >
              {verBorradas ? "Ocultar borradas" : `Ver borradas (${borradas.size})`}
            </Button>
            {borradas.size > 0 && verBorradas && (
              <Button
                size="small"
                appearance="subtle"
                onClick={() => restaurarBorradas(emailActual)}
              >
                Restaurar todas
              </Button>
            )}
          </div>
        )}

        {totalVisible === 0 ? (
          <div style={{ padding: "1rem", color: "#666", fontSize: 13, textAlign: "center" }}>
            ✓ Sin alertas activas que te conciernan.
          </div>
        ) : (
          <div>
            {visibles.map((it) => {
              const esLeida = leidas.has(it.id);
              const esBorrada = borradas.has(it.id);
              return (
                <div
                  key={it.id}
                  style={{
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid #f0f0f0",
                    borderLeft: `4px solid ${it.color}`,
                    fontSize: 12,
                    background: esBorrada ? "#F3F4F6" : "white",
                    opacity: esLeida && !esBorrada ? 0.6 : esBorrada ? 0.55 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 6,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: it.color }}>{it.label}</div>
                      <div style={{ color: "#444", marginTop: 2 }}>{it.detalle}</div>
                    </div>
                    <div style={{ display: "flex", gap: 2 }}>
                      {!esLeida && !esBorrada && (
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<CheckmarkRegular />}
                          onClick={() => marcarLeida(emailActual, it.id)}
                          title="Marcar como leída"
                          style={{ minWidth: 0, padding: "0 4px" }}
                        />
                      )}
                      {!esBorrada && (
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<DismissRegular />}
                          onClick={() => borrarAlerta(emailActual, it.id)}
                          title="Borrar"
                          style={{ minWidth: 0, padding: "0 4px" }}
                        />
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      paddingTop: 4,
                      borderTop: "1px dashed #e5e5e5",
                      fontSize: 11,
                      color: "#777",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "2px 10px",
                    }}
                  >
                    <span>🕐 {it.fechaHora}</span>
                    <span>De: {it.generadoPor}</span>
                    <span>Para: {it.dirigidoA}</span>
                  </div>
                </div>
              );
            })}
            <div style={{ padding: "0.4rem 0.75rem", fontSize: 11, color: "#999" }}>
              <Text size={100} style={{ color: "#999" }}>
                Filtradas a tu rol y autorizaciones. Computadas en vivo.
              </Text>
            </div>
          </div>
        )}
      </MenuPopover>
    </Menu>
  );
}
